/**
 * SpotifyBrowsePanel — the tabbed library browser (Lists / Liked / Top / Recent)
 * plus the collapsible "default playlist URL" editor at the bottom.
 *
 * The tabs swipe-cycle on mobile via `tabSwipeBind` (from `useSpotifyPlayerState`).
 */
import React from "react";
import { ListMusic, Heart, Clock, TrendingUp, Loader2 } from "lucide-react";
import SpotifyTrackRow from "./SpotifyTrackRow";
import { BROWSE_TABS } from "./useSpotifyPlayerState";

// Icon map kept in this file because lucide imports were colocated with labels
// originally; keeping them out of the hook so the hook stays JSX-free.
const TAB_ICONS = {
  playlists: ListMusic,
  liked: Heart,
  top: TrendingUp,
  recent: Clock,
};

export default function SpotifyBrowsePanel({
  tab, setTab,
  tabItems, tabLoading, tabSwipeBind,
  onPlay,
  current,
  urlEditing, setUrlEditing,
  url, setUrl,
  savingUrl, saveUrl,
}) {
  return (
    <>
      {/* Tabs */}
      <div className="flex items-center gap-1 mb-2 overflow-x-auto -mx-1">
        {BROWSE_TABS.map(({ key, label }) => {
          const I = TAB_ICONS[key];
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                tab === key
                  ? "bg-white text-black"
                  : "bg-white/5 hover:bg-white/10 text-white/70"
              }`}
              data-testid={`spotify-tab-${key}`}
            >
              <I className="w-3 h-3" /> {label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div
        className="max-h-72 overflow-y-auto rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl"
        data-testid={`spotify-tab-${tab}-content`}
        {...tabSwipeBind}
      >
        {tabLoading ? (
          <div className="p-4 text-white/40 text-sm inline-flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : tabItems.length === 0 ? (
          <div className="p-4 text-white/40 text-sm italic">Nothing here yet.</div>
        ) : (
          tabItems.map((it, i) => (
            <SpotifyTrackRow
              key={`${tab}-${it.id}-${i}`}
              item={it}
              kind={tab}
              onPlay={onPlay}
              testid={`spotify-${tab}-${i}`}
            />
          ))
        )}
      </div>

      {/* Default-playlist URL editor */}
      <div className="mt-2 flex items-center gap-2 text-xs text-white/40">
        {urlEditing ? (
          <>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://open.spotify.com/playlist/..."
              className="flex-1 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs focus:outline-none focus:border-[#1DB954]"
              data-testid="spotify-url-input"
            />
            <button
              onClick={saveUrl}
              disabled={savingUrl}
              className="text-[#1DB954] hover:text-white disabled:opacity-50"
              data-testid="spotify-url-save"
            >
              {savingUrl ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setUrlEditing(false)} className="text-white/40 hover:text-white">
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => setUrlEditing(true)}
            className="hover:text-white"
            data-testid="spotify-edit-toggle"
          >
            {current ? "Change default URL" : "Set default playlist URL"}
          </button>
        )}
      </div>
    </>
  );
}
